import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useContext, useState, useMemo } from 'react'

// others
import { BillDeliveryMethod, ResendBillToClient, useResendBillToClientMutation } from '../../../../../../generated'

import { Alert } from '../../../../../components/common/Alert'
import { CustomModal } from '../../../../../components/common/CustomModal'
import { AuthContext } from '../../../../../context'
import { ClientDeliveryType, DeliveryDetailProps } from '../../../../../interfaceTypes/props'
import { hasAdminAccess } from '../../../../../utils'

interface DeliveryRowsProps extends Partial<DeliveryDetailProps> {
  record: ClientDeliveryType
}
export const DeliveryRows = ({ record, refetch, delivery }: DeliveryRowsProps) => {
  const { currentUser } = useContext(AuthContext)

  const [openFailureModal, setOpenFailureModal] = useState<Record<string, boolean>>({})
  const [resending, setResending] = useState<Record<string, boolean>>({})

  const [resendBill] = useResendBillToClientMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { resendBillToClient } = data
      const { response } = resendBillToClient || {}
      const { status, message } = response || {}
      if (status) {
        Alert.success(message || '')
      }
    }
  })

  const handleResend = async (data: ResendBillToClient, timestamp: string) => {
    const { clientName } = data
    setResending(prev => ({ ...prev, [clientName + timestamp]: true }))
    await resendBill({
      variables: {
        resendBillToClient: {
          ...data
        }
      }
    })
    setResending(prev => ({ ...prev, [clientName + timestamp]: false }))

    refetch?.()
  }

  // Memoize for performance
  const shouldShowResend = useMemo(() => {
    // Only applies to failed deliveries
    if (!record.error) return false
    // If delivery or clientDeliveries not available, fallback to show resend
    if (!delivery?.clientDeliveries || !Array.isArray(delivery.clientDeliveries)) return false

    // Find if there is a later (timestamp > this row) successful delivery for same client and mode
    // Timestamps are assumed to be ISO strings, so string comparison works
    return !delivery.clientDeliveries.some(
      clientDelivery =>
        clientDelivery.name === record.name && clientDelivery.method === record.method && !clientDelivery.error
    )
  }, [record, delivery])

  return (
    <>
      <Box
        // key={idx}
        display="flex"
        alignItems="center"
        p={1}
        borderBottom={1}
        borderColor="#e0e0e0"
        bgcolor="ButtonShadow"
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 0.5, sm: 0 },
          position: 'relative'
        }}
      >
        <Typography
          fontSize={{ xs: 13, sm: 14 }}
          fontWeight={'medium'}
          width={{ xs: '100%', sm: '25%' }}
          sx={{ wordBreak: 'break-word' }}
        >
          {record.name}
        </Typography>

        <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'}>
          <Typography fontSize={{ xs: 13, sm: 14 }} sx={{ wordBreak: 'break-word', mr: 0.5 }}>
            {record.isReminder ? 'Reminder' : 'Delivery'}
          </Typography>

          <Typography
            fontSize={{ xs: 13, sm: 14 }}
            sx={{
              wordBreak: 'break-word',
              mx: 0.5,
              color: !record.error ? '#2e7d32' : '#d32f2f',
              fontWeight: 'bold',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
                opacity: 0.8
              }
            }}
            onClick={() =>
              setOpenFailureModal(prev => ({
                ...prev,
                [record.name + record.timestamp + record.method + delivery?.success]: true
              }))
            }
          >
            {!record.error ? 'Success' : 'Failed'}
          </Typography>

          <Typography fontSize={{ xs: 13, sm: 14 }} sx={{ wordBreak: 'break-word', ml: 0.5 }}>
            {`through ${record.method} at ${record.timestamp}`}
          </Typography>
        </Box>

        <CustomModal
          title={!record.error ? 'Delivery Content' : 'Delivery Failure'}
          isOpen={openFailureModal[record.name + record.timestamp + record.method + delivery?.success]}
          handleClose={() =>
            setOpenFailureModal(prev => ({
              ...prev,
              [record.name + record.timestamp + record.method + delivery?.success]: false
            }))
          }
        >
          {!record.error ? (
            <Typography>{!!record.content ? record.content : 'Bill Delivered via Email'}</Typography>
          ) : (
            <>
              <Typography fontWeight={'medium'}>{`Mode: ${record.error?.mode}`}</Typography>
              <Typography>{record.error?.message}</Typography>
            </>
          )}
        </CustomModal>
        <Box
          sx={{
            ml: { xs: 0, sm: 'auto' },
            mt: { xs: 1, sm: 0 },
            width: { xs: '100%', sm: 'auto' },
            display: 'flex',
            justifyContent: { xs: 'flex-end', sm: 'flex-end' }
          }}
        >
          {!!record.error && hasAdminAccess(currentUser) && shouldShowResend && (
            <Button
              variant="outlined"
              disabled={resending[record.name + record.timestamp]}
              endIcon={resending[record.name + record.timestamp] && <CircularProgress size={20} color="inherit" />}
              onClick={() =>
                handleResend(
                  {
                    billId: delivery?.billNumber || 0,
                    clientName: record.name,
                    mode: record.method.toLocaleUpperCase() as BillDeliveryMethod,
                    clientRole: record.role
                  },
                  record.timestamp
                )
              }
              sx={{
                minWidth: 70,
                fontWeight: 500,
                fontSize: { xs: 12, sm: 13 },
                borderRadius: 2,
                boxShadow: 'none',
                textTransform: 'none',
                px: 1.5,
                py: 0.5,
                backgroundColor: '#fff',
                color: '#000',
                borderColor: '#000',
                borderWidth: 1,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#000',
                  color: '#000'
                }
              }}
            >
              Resend
            </Button>
          )}
        </Box>
      </Box>
    </>
  )
}
